import { gameNames } from "abstract/constants/game-ids"
import { TIME_BASED_GAMES } from "./timeBasedGamesList"

export const TIME_BASED_ID_MESSAGE = "Not a game ID"

/** Fired by buff.app when a time-based game closes. */
export const SESSION_CLOSE_TRIGGER = "session_close_notification"

export const describeInvalidId = (id: string): string => {
  if (!id.trim()) return "an empty entry"
  if (!/^\d+$/.test(id.trim())) return `"${id}" is not a number`

  return `"${id}" has a leading zero — use ${String(Number(id.trim()))}`
}

/** True when the id is a real Overwolf class id we ship, as opposed to one typed from memory. */
export const isListedTimeBasedGame = (gameId: string | number): boolean => {
  const canonical = normalizeStoredGameId(gameId)

  return canonical !== undefined && Object.prototype.hasOwnProperty.call(TIME_BASED_NAMES, canonical)
}

/**
 * Every display and preset site keys by `Number(gameId)` while the client compares the stored
 * string strictly, so "021566" would render as APEX and deliver to nobody. One spelling per game.
 */
export const canonicalGameId = (gameId: string | number): string | undefined => {
  const canonical = normalizeStoredGameId(gameId)

  return canonical === String(gameId).trim() ? canonical : undefined
}

/**
 * The lenient half, for values already in the config: a hand-edited "07314" is repaired to "7314"
 * and keeps its banner, rather than being dropped and replaced with a bare entry.
 */
export const normalizeStoredGameId = (gameId: string | number): string | undefined => {
  const trimmed = String(gameId).trim()

  return /^\d+$/.test(trimmed) ? String(Number(trimmed)) : undefined
}

export const isCatalogGameId = (gameId: string | number): boolean => {
  const canonical = normalizeStoredGameId(gameId)

  // Classified and canonicalisable must agree: accepting anything Number() parses let "07314"
  // count as catalog while canonicalGameId rejected it, and the entry was then dropped on sync.
  return canonical !== undefined && Object.prototype.hasOwnProperty.call(gameNames, canonical)
}

/** Class id * 10 + index is Overwolf's instance id, and it is what a dev-tools lookup shows. */
export const instanceIdOfCatalogGame = (gameId: string): string | undefined => {
  const canonical = canonicalGameId(gameId)
  if (canonical === undefined) return undefined

  // A real game id is never an instance id, whichever list names it. 26840 is 2XKO and 2684 is
  // The Lord of the Rings Online: without this, picking 2XKO was refused with an error telling
  // ops to drop the last digit, which lands them on a different game that validates clean.
  if (isCatalogGameId(canonical) || isListedTimeBasedGame(canonical)) return undefined

  const classId = Math.floor(Number(canonical) / 10)
  if (classId === 0) return undefined

  // The shipped list counts as a source: 213021 is the instance id of Geometry Dash, and that is
  // the likelier paste now that the picker exists.
  return isCatalogGameId(classId) ? gameNames[classId] : TIME_BASED_NAMES[String(classId)]
}

const TIME_BASED_NAMES: Record<string, string> = TIME_BASED_GAMES.reduce<Record<string, string>>(
  (acc, [id, name]) => {
    acc[id] = name

    return acc
  },
  {},
)

const TIME_BASED_NOTES: Record<string, string> = TIME_BASED_GAMES.reduce<Record<string, string>>(
  (acc, [id, , , note]) => {
    if (note) acc[id] = note

    return acc
  },
  {},
)

export const TIME_BASED_GAME_OPTIONS = TIME_BASED_GAMES.map(([id, name, installs, note]) => ({
  value: id,
  // The caution rides in the label because antd filters and renders on it, so ops sees the reason
  // in the dropdown rather than finding out after the campaign under-delivers.
  label: note ? `${id} — ${name} (${note})` : `${id} — ${name}`,
  installs,
  note,
}))

/** Reason this game may not deliver, for a game that carries one. Nothing is hidden on its account. */
export const timeBasedGameCaution = (gameId: string | number): string | undefined => {
  const canonical = normalizeStoredGameId(gameId)

  return canonical ? TIME_BASED_NOTES[canonical] : undefined
}

/** Known-game name where there is one, else the raw id so a typo can be proofread. */
export const gameLabel = (gameId: string | number | undefined): string => {
  const canonical = normalizeStoredGameId(gameId ?? "")

  return gameNames[Number(gameId)] ?? (canonical ? TIME_BASED_NAMES[canonical] : undefined) ?? `Game ${gameId}`
}

/**
 * `games` and `notificationsPerGame` hold catalog and time-based ids together: the import validator
 * requires the two to be set-equal. The live client keys only on notificationsPerGame. The two
 * editors are split only so a typo in one cannot deselect a game in the other.
 */
export const splitGameIds = (games: string[] | undefined): { catalog: string[], timeBased: string[] } => {
  const all = (games ?? []).map(gameId => normalizeStoredGameId(gameId) ?? String(gameId))

  return {
    catalog: all.filter(isCatalogGameId),
    timeBased: all.filter(gameId => !isCatalogGameId(gameId)),
  }
}

export const mergeGameIds = (catalog: string[] | undefined, timeBased: string[] | undefined): string[] => {
  const all = [...(catalog ?? []), ...(timeBased ?? [])].map(gameId => normalizeStoredGameId(gameId) ?? String(gameId))

  return Array.from(new Set(all))
}

export const invalidTimeBasedIds = (ids: string[] | undefined): string[] => (
  (ids ?? []).map(String).filter(id => canonicalGameId(id) === undefined)
)

export const catalogIdsAmongTimeBased = (ids: string[] | undefined): string[] => (
  (ids ?? []).map(String).filter(isCatalogGameId)
)

/** Ids that look like `<catalog class id><index>`, reported as `id → game name`. */
export const instanceIdsAmongTimeBased = (ids: string[] | undefined): string[] => (
  (ids ?? [])
    .map(String)
    .filter(id => !isCatalogGameId(id))
    .map(id => {
      const game = instanceIdOfCatalogGame(id)

      return game ? `${id} → ${game}` : undefined
    })
    .filter((entry): entry is string => entry !== undefined)
)

/**
 * One notificationsPerGame list is fed by two editors, so a sync must be given both sides:
 * filtering on one field's ids alone drops every game the other field owns. Unparseable ids are
 * left out entirely — the field error already blocks the save, and materialising them here builds
 * a `NaN` card in the Assets and Pixels panels whose config is discarded when the tag is fixed.
 */
export const syncedNotificationsPerGame = <T extends { gameId: string | number }>(
  current: T[],
  catalogIds: (string | number)[],
  timeBasedIds: (string | number)[],
): (T | { gameId: string })[] => {
  const selected = [...catalogIds, ...timeBasedIds]
    .map(canonicalGameId)
    .filter((gameId): gameId is string => gameId !== undefined)

  const canonicalOf = (entry: T | { gameId: string }) => normalizeStoredGameId(entry.gameId)
  const kept: (T | { gameId: string })[] = current
    .filter(entry => {
      const canonical = canonicalOf(entry)

      return canonical !== undefined && selected.includes(canonical)
    })
    // Rewritten only when the spelling actually differs, so an untouched entry keeps its identity.
    .map(entry => (String(entry.gameId) === canonicalOf(entry) ? entry : { ...entry, gameId: canonicalOf(entry) as string }))

  selected.forEach(gameId => {
    if (!kept.some(entry => canonicalOf(entry) === gameId)) kept.push({ gameId })
  })

  return kept
}

/** A game with no in-game events: session-only, and the client refuses every reward on that path. */
export const isTimeBasedGameId = (gameId: string | number | undefined): boolean => {
  if (gameId === undefined || gameId === null || String(gameId).trim() === "") return false

  return !isCatalogGameId(gameId)
}

/**
 * The shape the edit form holds while a campaign is open: `games` drives the catalog select and
 * `timeBasedGames` the picker. Extracted from the page so both directions are testable — the
 * round trip is what decides whether a saved campaign keeps its games.
 */
export const gameFieldsFromEvent = (games: string[] | undefined): { games: string[], timeBasedGames: string[] } => {
  const { catalog, timeBased } = splitGameIds(games)

  return { games: catalog, timeBasedGames: timeBased }
}

export const gamesForSave = (games: string[] | undefined, timeBasedGames: string[] | undefined): string[] => (
  mergeGameIds(games, timeBasedGames)
)
